import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  createUserWithEmailAndPassword 
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { auth, db } from './firebaseConfig';

class StaffAuthManager {
  constructor() {
    this.currentEmployee = null;
    this.authState = null;
    this.unsubscribe = null;
  }

  // Check if bypass mode is enabled
  isBypassMode() {
    return process.env.REACT_APP_BYPASS_STAFF_AUTH === 'true';
  }

  // Get mock employee data for bypass mode
  getMockEmployee() {
    return {
      uid: 'bypass-employee-001',
      email: 'test@beemascaffolding.sg',
      displayName: 'Test Employee',
      employeeId: 'EMP001',
      department: 'Operations',
      isActive: true,
      createdAt: new Date().toISOString(),
      lastLoginAt: new Date().toISOString()
    };
  }

  // Bypass login method
  async bypassLogin() {
    if (!this.isBypassMode()) {
      throw new Error('Bypass mode is not enabled');
    }

    this.currentEmployee = this.getMockEmployee();
    this.authState = 'authenticated';

    return {
      success: true,
      employee: this.currentEmployee
    };
  }

  // Initialize auth state listener
  initializeAuthListener(callback) {
    this.unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Check if user exists in employees collection
        const employeeDoc = await getDoc(doc(db, 'employees', user.uid));
        if (employeeDoc.exists()) {
          const employeeData = employeeDoc.data();
          if (employeeData.isActive) {
            this.currentEmployee = {
              uid: user.uid,
              email: user.email,
              ...employeeData
            };
            this.authState = 'authenticated';
          } else {
            // Employee account is inactive
            this.currentEmployee = null;
            this.authState = 'inactive';
          }
        } else {
          // User doesn't exist in employees collection
          this.currentEmployee = null;
          this.authState = 'unauthorized';
        }
      } else {
        this.currentEmployee = null;
        this.authState = 'unauthenticated';
      }
      
      if (callback) callback(this.authState, this.currentEmployee);
    });
  }

  // Employee login
  async employeeLogin(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Check if user exists in employees collection
      const employeeDoc = await getDoc(doc(db, 'employees', user.uid));
      if (!employeeDoc.exists()) {
        await signOut(auth);
        throw new Error('Access denied. Employee credentials required.');
      }

      const employeeData = employeeDoc.data();
      if (!employeeData.isActive) {
        await signOut(auth);
        throw new Error('Employee account is inactive. Please contact administrator.');
      }

      // Update last login timestamp
      await setDoc(doc(db, 'employees', user.uid), {
        ...employeeData,
        lastLoginAt: serverTimestamp()
      }, { merge: true });

      this.currentEmployee = {
        uid: user.uid,
        email: user.email,
        ...employeeData
      };

      return {
        success: true,
        employee: this.currentEmployee
      };
    } catch (error) {
      console.error('Employee login error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Employee logout
  async employeeLogout() {
    try {
      await signOut(auth);
      this.currentEmployee = null;
      this.authState = 'unauthenticated';
      return { success: true };
    } catch (error) {
      console.error('Employee logout error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get current employee
  getCurrentEmployee() {
    return this.currentEmployee;
  }

  // Check if user is authenticated employee
  isAuthenticated() {
    return this.authState === 'authenticated' && this.currentEmployee !== null;
  }

  // Get auth state
  getAuthState() {
    return this.authState;
  }

  // Check if user has employee privileges
  async isEmployee(user) {
    if (!user) return false;

    try {
      const employeeDoc = await getDoc(doc(db, 'employees', user.uid));
      return employeeDoc.exists() && employeeDoc.data().isActive;
    } catch (error) {
      console.error('Error checking employee status:', error);
      return false;
    }
  }

  // Clean up auth listener
  cleanup() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  // Create employee profile (admin function)
  async createEmployeeProfile(employeeData) {
    try {
      const { email, password, displayName, employeeId, department } = employeeData;
      
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Create employee profile in Firestore
      const profileData = {
        uid: user.uid,
        email: user.email,
        displayName: displayName || '',
        employeeId: employeeId || '',
        department: department || '',
        isActive: true,
        createdAt: serverTimestamp(),
        lastLoginAt: null
      };

      await setDoc(doc(db, 'employees', user.uid), profileData);

      return {
        success: true,
        employee: profileData
      };
    } catch (error) {
      console.error('Create employee profile error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

// Create singleton instance
const staffAuthManager = new StaffAuthManager();

export default staffAuthManager;
export { StaffAuthManager };
